import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { Reveal, Overline } from "../components/Reveal";
import { CTASection } from "../components/home/CTASection";
import { IMG, COMPANY } from "../data/content";

const DETAIL = [
  {
    id: "horticultural",
    num: "01",
    eyebrow: "Horticultural Services",
    title: "Creating Beautiful, Healthy & Sustainable Landscapes",
    copy: "A well-maintained landscape enhances the appearance, value and functionality of any property. Our horticultural services provide professional gardening, landscaping and grounds maintenance for commercial, industrial, residential and public environments.",
    image: IMG.gardener,
    serve: ["Office buildings", "Retail centres", "Industrial facilities", "Residential estates", "Sports & recreational grounds", "Resorts, hotels & lodges", "Parks & public spaces", "Educational institutions"],
    services: ["Lawn maintenance & grass cutting", "Garden design & landscaping", "Tree & shrub pruning", "Weed control & prevention", "Flower bed installation & maintenance", "Irrigation system maintenance", "Fertilisation & soil improvement", "Seasonal planting programmes", "Grounds maintenance & site beautification", "Pest & disease management"],
    tags: ["Environmental Responsibility", "Plant Health", "Biodiversity", "Water Efficiency", "Aesthetic Excellence", "Human Well-being"],
  },
  {
    id: "cleaning",
    num: "02",
    eyebrow: "Professional Cleaning",
    title: "Reliable Cleaning Solutions for Every Environment",
    copy: "Clean premises contribute to employee productivity, customer confidence, health and safety. SML provides professional contract cleaning services tailored to each client's requirements.",
    image: IMG.floorIndustrial,
    serve: ["Corporate offices", "Retail stores & centres", "Industrial & manufacturing", "Schools, colleges & universities", "Hospitality & accommodation", "Construction & renovation", "Residential estates", "Medical & healthcare"],
    services: ["General cleaning (daily / weekly / monthly)", "Interior & exterior window cleaning", "Carpet cleaning & stain removal", "Deep cleaning of high-touch areas", "Event & project cleaning", "Pre-occupation & post-construction cleaning", "Floor scrubbing, stripping & sealing", "Floor polishing, buffing & maintenance"],
    tags: ["Trained Teams", "Supervised Delivery", "Flexible Programmes", "Health & Safety"],
  },
  {
    id: "hygiene",
    num: "03",
    eyebrow: "Hygiene Solutions",
    title: "Promoting Healthier & Safer Environments",
    copy: "Our hygiene management solutions help organisations maintain clean, healthy and hygienic facilities through professional installation, servicing and replenishment programmes.",
    image: IMG.washroom,
    serve: ["Soap dispensers", "Toilet tissue dispensers", "Hand towel dispensers", "Air freshener dispensers", "Sanitary waste disposal units", "Hand sanitiser stations"],
    services: ["Toilet tissue", "Hand towels", "Liquid soap", "Hand sanitiser", "Air freshener refills", "Sanitary bin liners", "Scheduled inspections & replenishment"],
    serveLabel: "Hygiene Equipment",
    servicesLabel: "Consumables & Servicing",
    tags: ["Disease Prevention", "Healthy Workplaces", "Consistent Standards", "Compliance Support", "Employee Well-being"],
  },
  {
    id: "supplies",
    num: "04",
    eyebrow: "Cleaning & Hygiene Supplies",
    title: "Quality Products for Professional Results",
    copy: "In addition to our services, SML supplies a comprehensive range of cleaning chemicals, janitorial equipment, hygiene products and cleaning accessories — plus PPE.",
    image: IMG.supplies,
    serve: ["Multi-purpose cleaners", "Degreasers", "Disinfectants & sanitisers", "Floor care products", "Glass cleaners", "Industrial cleaning solutions"],
    services: ["Vacuum cleaners & scrubbers", "Polishers & burnishers", "Pressure washers", "Carpet cleaning machines", "Mops, buckets & trolleys", "Gloves, masks & protective clothing"],
    serveLabel: "Cleaning Chemicals",
    servicesLabel: "Equipment, Janitorial & PPE",
    external: true,
    tags: ["Trusted Brands", "Bulk Supply", "Reliable Delivery"],
  },
];

const ServiceBlock = ({ d, i }) => {
  const reversed = i % 2 === 1;
  return (
    <section id={d.id} data-testid={`service-detail-${d.id}`} className={`scroll-mt-24 py-20 lg:py-28 ${i % 2 === 1 ? "bg-sml-mist" : "bg-white"}`}>
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid gap-12 lg:grid-cols-12 lg:gap-16 items-center">
        <div className={`lg:col-span-5 ${reversed ? "lg:order-2" : ""}`}>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-sm"
          >
            <img src={d.image} alt={d.title} className="h-[420px] w-full object-cover" />
            <span className="absolute top-5 left-5 font-display text-6xl text-white/90 mix-blend-difference">{d.num}</span>
          </motion.div>
        </div>
        <div className={`lg:col-span-7 ${reversed ? "lg:order-1" : ""}`}>
          <Overline>{d.eyebrow}</Overline>
          <Reveal>
            <h2 className="mt-5 font-display text-3xl md:text-4xl tracking-tight text-sml-charcoal text-balance">{d.title}</h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mt-6 text-sml-charcoal/70 leading-relaxed max-w-2xl">{d.copy}</p>
          </Reveal>
          <div className="mt-8 grid sm:grid-cols-2 gap-8">
            <div>
              <h3 className="font-mono-sml text-[11px] uppercase tracking-[0.18em] text-sml-green mb-4">{d.serveLabel || "We Serve"}</h3>
              <ul className="space-y-2.5">
                {d.serve.map((s) => (
                  <li key={s} className="flex items-start gap-2.5 text-sm text-sml-charcoal/75">
                    <Check className="h-4 w-4 mt-0.5 text-sml-bronze shrink-0" /> {s}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-mono-sml text-[11px] uppercase tracking-[0.18em] text-sml-green mb-4">{d.servicesLabel || "Services Include"}</h3>
              <ul className="space-y-2.5">
                {d.services.map((s) => (
                  <li key={s} className="flex items-start gap-2.5 text-sm text-sml-charcoal/75">
                    <Check className="h-4 w-4 mt-0.5 text-sml-bronze shrink-0" /> {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-2">
            {d.tags.map((t) => (
              <span key={t} className="font-mono-sml text-[10px] uppercase tracking-[0.14em] bg-sml-forest/5 text-sml-forest rounded-sm px-3 py-1.5">{t}</span>
            ))}
          </div>
          {d.external ? (
            <a href={COMPANY.productUrl} target="_blank" rel="noopener noreferrer" data-testid="services-product-link" className="group mt-8 inline-flex items-center gap-2 rounded-sm bg-sml-forest text-white font-medium px-6 py-3.5 hover:bg-sml-green transition-colors">
              Browse Our Product Range <ArrowUpRight className="h-4 w-4" />
            </a>
          ) : (
            <Link to="/contact" data-testid={`services-cta-${d.id}`} className="group mt-8 inline-flex items-center gap-2 rounded-sm bg-sml-forest text-white font-medium px-6 py-3.5 hover:bg-sml-green transition-colors">
              Request a Quote <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export default function Services() {
  return (
    <main data-testid="services-page">
      <PageHeader
        eyebrow="Our Services"
        title="Integrated Property Support Services"
        subtitle="From landscaped grounds to spotless facilities and fully stocked hygiene environments — comprehensive services designed around the needs of your property."
        image={IMG.lobby}
      />
      <div className="bg-white border-b border-sml-border sticky top-[64px] z-30 backdrop-blur-xl bg-white/85">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 flex gap-6 overflow-x-auto py-4">
          {DETAIL.map((d) => (
            <a key={d.id} href={`#${d.id}`} data-testid={`services-anchor-${d.id}`} className="whitespace-nowrap text-sm font-medium text-sml-charcoal/70 hover:text-sml-forest transition-colors">
              {d.eyebrow}
            </a>
          ))}
        </div>
      </div>
      {DETAIL.map((d, i) => <ServiceBlock key={d.id} d={d} i={i} />)}
      <CTASection />
    </main>
  );
}
